import { useState, useEffect } from 'react';
import { getLessonVideoUrl } from '@/lib/api';
import { toEmbedUrl } from '@/lib/lessonMedia';
import { PlayCircle, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Renders a lesson's video.
 *
 * Two shapes, chosen by `lesson.video_type`:
 *  - "embed"  → the instructor pasted a provider link; normalised and iframed.
 *  - "upload" → a clip in our R2 bucket; the object key never reaches the
 *               browser, so we exchange the lesson id for a short-lived
 *               presigned URL and hand that to a native <video>.
 *
 * Anything else renders nothing, so a text lesson stays text.
 */
export default function LessonVideoPlayer({ lesson }) {
  const [uploadUrl, setUploadUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const lessonId = lesson?.id;
  const videoType = lesson?.video_type || (lesson?.video_url ? 'embed' : 'none');

  useEffect(() => {
    if (videoType !== 'upload' || !lessonId) {
      setUploadUrl('');
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const res = await getLessonVideoUrl(lessonId);
        if (!cancelled) setUploadUrl(res.data?.url || '');
      } catch (err) {
        console.error(err);
        if (!cancelled) setError('This video could not be loaded. Please refresh and try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [lessonId, videoType]);

  if (videoType === 'none' || (!lesson?.video_url && videoType !== 'upload')) {
    return null;
  }

  const frame = 'aspect-video w-full bg-[#062B1A] rounded-t-xl overflow-hidden';

  if (videoType === 'upload') {
    if (loading) {
      return (
        <div className={`${frame} flex items-center justify-center`}>
          <Loader2 className="w-8 h-8 text-[#D4AF37]/60 animate-spin" />
        </div>
      );
    }
    if (error || !uploadUrl) {
      return (
        <div className={`${frame} flex flex-col items-center justify-center gap-2 px-6 text-center`}>
          <AlertTriangle className="w-8 h-8 text-[#D4AF37]/70" />
          <p className="text-sm text-white/70">{error || 'This video is not available yet.'}</p>
        </div>
      );
    }
    return (
      <div className={frame}>
        {/* `key` forces a reload when the presigned URL is refreshed. */}
        <video
          key={uploadUrl}
          className="h-full w-full"
          controls
          controlsList="nodownload"
          preload="metadata"
          data-testid="lesson-video-upload"
        >
          <source src={uploadUrl} type={lesson?.video_content_type || 'video/mp4'} />
          Your browser cannot play this video.
        </video>
      </div>
    );
  }

  const embedUrl = toEmbedUrl(lesson?.video_url);
  if (!embedUrl) {
    return (
      <div className={`${frame} flex flex-col items-center justify-center gap-2 px-6 text-center`}>
        <AlertTriangle className="w-8 h-8 text-[#D4AF37]/70" />
        <p className="text-sm text-white/70">This lesson's video link is not valid.</p>
      </div>
    );
  }

  return (
    <div className={frame}>
      <iframe
        key={embedUrl}
        src={embedUrl}
        title={lesson?.title || 'Lesson video'}
        className="h-full w-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        data-testid="lesson-video-embed"
      />
    </div>
  );
}

/** Small inline badge used in lists to mark a lesson that carries video. */
export function VideoIndicator({ lesson }) {
  const hasVideo =
    lesson?.video_type === 'upload' || Boolean(lesson?.video_url) || lesson?.type === 'video';
  if (!hasVideo) return null;
  return <PlayCircle className="w-3 h-3 text-[#D4AF37]" aria-label="Has video" />;
}
